import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Send, Plus, Trash2, Eye, X, TestTube, FileText, Settings, RefreshCw, Edit3, Save, Users, Building, Search, CheckSquare, Square, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import SendingProgressModal from '@/components/SendingProgressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdvertisementProposal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState('');
  
  // Custom HTML mode state
  const [useCustomHtml, setUseCustomHtml] = useState(false);
  const [customHtml, setCustomHtml] = useState('');
  
  // Recipient selection state
  const [recipientMode, setRecipientMode] = useState('manual'); // 'manual', 'members', 'advertisers'
  const [members, setMembers] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  
  // Editable content for preview - these can be customized per recipient
  const [editableContent, setEditableContent] = useState({
    introduction: '',
    why_advertise: '',
    terms_conditions: '',
    special_remarks: '',
    circulation_text: '',
    signature_text: ''
  });
  
  // Sender accounts state
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  
  // PDF Attachment toggle (default ON)
  const [attachPdf, setAttachPdf] = useState(true);
  
  const [formData, setFormData] = useState({
    magazine: 'Aabkari Times',
    proposal_type: 'standard_tariff',
    contact_name: '',
    company_name: '',
    emails: '',
    proposal_date: new Date().toISOString().split('T')[0],
    ad_positions: [],
    discount_percent: 0,
    terms_conditions: '',
    special_remarks: '',
    media_kit_url: '',
    cover_letter_url: '',
    banner_image_url: '',
    sender_email: '',  // Selected sender email
    sender_name: ''    // Selected sender display name
  });

  const proposalTypes = [
    { value: 'standard_tariff', label: 'Standard Advertisement Proposal (Standard Tariff)' },
    { value: 'standard_tariff_2', label: 'Standard Advertisement Proposal (Frequency Discount Tariff)' },
    { value: 'custom_tariff', label: 'Standard Advertisement Proposal (Custom Tariff)' },
    { value: 'republic_day', label: 'Republic Day Special Issue' },
    { value: 'independence_day', label: 'Independence Day Special Issue' },
    { value: 'holi', label: 'Holi Special Issue' },
    { value: 'diwali', label: 'Diwali Special Issue' },
    { value: 'custom', label: 'Other Custom Proposal' }
  ];

  // Frequency Discount Settings (editable discounts and visible columns)
  const [frequencySettings, setFrequencySettings] = useState({
    threeMonthDiscount: 10,
    sixMonthDiscount: 20,
    oneYearDiscount: 30,
    showThreeMonth: true,
    showSixMonth: true,
    showOneYear: true
  });

  // Standard Tariff 2 - Frequency Discount Table Data (with 3 months)
  const frequencyTariffData = {
    'Aabkari Times': [
      { position: 'Front Cover Inside', oneMonth: 35000, threeMonths: 94500, sixMonths: 168000, oneYear: 294000 },
      { position: 'Back Cover Inside', oneMonth: 30000, threeMonths: 81000, sixMonths: 144000, oneYear: 252000 },
      { position: 'Back Cover', oneMonth: 40000, threeMonths: 108000, sixMonths: 192000, oneYear: 336000 },
      { position: 'Full Page Inside', oneMonth: 25000, threeMonths: 67500, sixMonths: 120000, oneYear: 210000 }
    ],
    'Sugar Times': [
      { position: 'Front Cover Inside', oneMonth: 30000, threeMonths: 81000, sixMonths: 144000, oneYear: 252000 },
      { position: 'Back Cover Inside', oneMonth: 25000, threeMonths: 67500, sixMonths: 120000, oneYear: 210000 },
      { position: 'Back Cover', oneMonth: 35000, threeMonths: 94500, sixMonths: 168000, oneYear: 294000 },
      { position: 'Full Page Inside', oneMonth: 20000, threeMonths: 54000, sixMonths: 96000, oneYear: 168000 }
    ]
  };

  // Check if it's a special issue type
  const isSpecialIssue = ['republic_day', 'independence_day', 'holi', 'diwali', 'custom'].includes(formData.proposal_type);
  const isFrequencyTariff = formData.proposal_type === 'standard_tariff_2';

  // Fetch members
  const fetchMembers = async () => {
    setLoadingRecipients(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/members/search`, {
        magazine: formData.magazine
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data || []);
    } catch (error) {
      console.error('Error fetching members:', error);
      toast.error('Failed to fetch members');
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Fetch advertisers
  const fetchAdvertisers = async () => {
    setLoadingRecipients(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/advertisers/search`, {
        magazine: formData.magazine
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdvertisers(response.data || []);
    } catch (error) {
      console.error('Error fetching advertisers:', error);
      toast.error('Failed to fetch advertisers');
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Handle recipient mode change
  const handleRecipientModeChange = (mode) => {
    setRecipientMode(mode);
    setSelectedRecipients([]);
    setRecipientSearch('');
    
    if (mode === 'members' && members.length === 0) {
      fetchMembers();
    } else if (mode === 'advertisers' && advertisers.length === 0) {
      fetchAdvertisers();
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (recipient, type) => {
    const id = recipient.id || recipient.member_id || recipient.advertiser_id;
    const isSelected = selectedRecipients.some(r => (r.id || r.member_id || r.advertiser_id) === id);
    
    if (isSelected) {
      setSelectedRecipients(selectedRecipients.filter(r => (r.id || r.member_id || r.advertiser_id) !== id));
    } else {
      setSelectedRecipients([...selectedRecipients, { ...recipient, type }]);
    }
  };

  // Select all filtered recipients
  const selectAllFiltered = (list, type) => {
    const filtered = getFilteredList(list);
    const newSelections = filtered.map(r => ({ ...r, type }));
    setSelectedRecipients(newSelections);
  };

  // Clear all selections
  const clearAllSelections = () => {
    setSelectedRecipients([]);
  };

  // Filter list by search
  const getFilteredList = (list) => {
    if (!recipientSearch) return list;
    const search = recipientSearch.toLowerCase();
    return list.filter(item => 
      (item.name || item.contact_person || '').toLowerCase().includes(search) ||
      (item.company_name || item.organization || '').toLowerCase().includes(search) ||
      (item.email || '').toLowerCase().includes(search) ||
      (item.member_id || '').toLowerCase().includes(search) ||
      (item.advertiser_id || '').toLowerCase().includes(search) ||
      (item.id || '').toLowerCase().includes(search)
    );
  };

  // Default content templates
  const getDefaultContent = (magazine) => {
    const isAabkari = magazine === 'Aabkari Times' || magazine === 'Aabkari Times ENG';
    const circulation = isAabkari ? '16,700+ copies per month' : '10,000+ copies per month';
    const businessManager = isAabkari ? 'Arvind Dubey' : 'Shashi Yogeshwar';
    
    return {
      introduction: isAabkari 
        ? `Greetings from Aabkari Times! Aabkari Times is a monthly news magazine on Excise & Alcobev that has been in continuous publication since 2009. It is backed by a team of retired Excise Department officials and seasoned professionals from the alco-bev industry.`
        : `Greetings from Sugar Times! Sugar Times is a monthly news magazine covering the sugar and ethanol industry with comprehensive coverage of policy updates, industry trends, and market insights.`,
      why_advertise: `Our magazine has 17+ years of history in the excise and alco-bev industry. Many companies have recognized the benefits of advertising with us. We are a trusted source for industry news and insights.`,
      terms_conditions: formData.terms_conditions || '',
      special_remarks: formData.special_remarks || '',
      circulation_text: `<strong>Circulation:</strong> ${circulation} | <strong>Reach:</strong> Distilleries, Breweries, Sugar Mills, Excise Bodies & Allied Industries.`,
      signature_text: `Looking forward to your positive response.<br/><br/>Best Regards,<br/><strong>${businessManager}</strong><br/><span style='font-size: 9px; color: #6b7280;'>Business Manager</span>`
    };
  };

  const positionOptions = [
    'Full Page (Inside)',
    'Half Page',
    'Quarter Page',
    'Back Cover',
    'Inside Front Cover',
    'Inside Back Cover',
    'Centre Spread',
    'Custom Position'
  ];

  const pageTypeOptions = [
    'Color',
    'Black & White',
    'Color (Print + Digital)',
    'Digital Only'
  ];

  const insertionOptions = [
    '1 Issue',
    '3 Issues',
    '6 Issues',
    '12 Issues',
    'Special Issue',
    'Custom'
  ];

  // Check for draft data from Copy/Forward action (from AdProposalReports)
  // Use ref to prevent race condition with loadDefaults
  const draftLoadedRef = useRef(false);
  const [draftInfo, setDraftInfo] = useState(null);
  
  useEffect(() => {
    const draftData = sessionStorage.getItem('proposalDraft');
    if (draftData) {
      try {
        const draft = JSON.parse(draftData);
        
        // Mark as draft loaded BEFORE setting state to prevent loadDefaults
        draftLoadedRef.current = true;
        setDraftInfo(draft);
        
        // Load draft into form - this takes priority over defaults
        setFormData(prev => ({
          ...prev,
          magazine: draft.magazine || prev.magazine,
          proposal_type: draft.proposal_type || prev.proposal_type,
          contact_name: draft.contact_name || '',
          company_name: draft.company_name || '',
          emails: draft.emails || '',
          proposal_date: draft.proposal_date || new Date().toISOString().split('T')[0],
          ad_positions: draft.ad_positions || [],
          discount_percent: draft.discount_percent || 0,
          terms_conditions: draft.terms_conditions || '',
          special_remarks: draft.special_remarks || '',
          banner_image_url: draft.banner_image_url || '',
          media_kit_url: draft.media_kit_url || '',
          cover_letter_url: draft.cover_letter_url || ''
        }));
        
        // Load editable content
        setEditableContent({
          introduction: draft.introduction || '',
          why_advertise: draft.why_advertise || '',
          terms_conditions: draft.terms_conditions || '',
          special_remarks: draft.special_remarks || ''
        });
        
        // Show toast based on action
        if (draft.action === 'copy') {
          toast.success(`Proposal copied! Original: ${draft.copied_from?.original_company || 'Unknown'}. Update recipient and send.`);
        } else if (draft.action === 'forward') {
          toast.success(`Forwarding proposal. Original: ${draft.forwarded_from?.original_company || 'Unknown'}. Enter new recipient.`);
        }
        
        // Clear draft from sessionStorage
        sessionStorage.removeItem('proposalDraft');
      } catch (e) {
        console.error('Error loading draft:', e);
        sessionStorage.removeItem('proposalDraft');
      }
    }
  }, []);

  useEffect(() => {
    // Skip loadDefaults if we're in the middle of loading a draft
    // The draftLoadedRef stays true for 500ms after draft load to handle React's batched updates
    if (draftLoadedRef.current) {
      // Don't reset immediately - let a small timeout handle it
      setTimeout(() => {
        draftLoadedRef.current = false;
      }, 500);
      return;
    }
    loadDefaults();
    fetchSenderAccounts();
    fetchTemplates();
  }, [formData.proposal_type, formData.magazine]);

  // Fetch sender accounts for the selected magazine
  const fetchSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/sender-accounts/${formData.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenderAccounts(response.data || []);
      
      // Set default sender if available
      const defaultSender = response.data?.find(a => a.is_default);
      if (defaultSender && !selectedSender) {
        setSelectedSender(defaultSender);
        setFormData(prev => ({
          ...prev,
          sender_email: defaultSender.email,
          sender_name: defaultSender.display_name
        }));
      }
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  // Fetch proposal templates
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ad-proposals/templates/${formData.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  // Load a template into the form
  const loadTemplate = async (templateId) => {
    if (!templateId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ad-proposals/templates/detail/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const template = response.data;
      
      setFormData(prev => ({
        ...prev,
        proposal_type: template.proposal_type || prev.proposal_type,
        ad_positions: template.ad_positions || [],
        terms_conditions: template.terms_conditions || '',
        special_remarks: template.special_remarks || '',
        banner_image_url: template.banner_image_url || '',
        media_kit_url: template.media_kit_url || '',
        cover_letter_url: template.cover_letter_url || ''
      }));
      
      // Also load editable content
      setEditableContent({
        introduction: template.introduction || '',
        why_advertise: template.why_advertise || '',
        terms_conditions: template.terms_conditions || '',
        special_remarks: template.special_remarks || ''
      });
      
      toast.success(`Template "${template.template_name}" loaded!`);
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    }
  };

  // Save current form as template
  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/ad-proposals/templates`, {
        template_name: templateName,
        template_category: ['republic_day', 'independence_day', 'holi', 'diwali'].includes(formData.proposal_type) ? 'special_issue' : 'custom',
        magazine: formData.magazine,
        proposal_type: formData.proposal_type,
        ad_positions: formData.ad_positions,
        terms_conditions: formData.terms_conditions,
        special_remarks: formData.special_remarks,
        introduction: editableContent.introduction,
        why_advertise: editableContent.why_advertise,
        banner_image_url: formData.banner_image_url,
        media_kit_url: formData.media_kit_url,
        cover_letter_url: formData.cover_letter_url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Template "${templateName}" saved!`);
      setShowSaveTemplate(false);
      setTemplateName('');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleSenderChange = (accountId) => {
    const account = senderAccounts.find(a => a.id === accountId);
    if (account) {
      setSelectedSender(account);
      setFormData(prev => ({
        ...prev,
        sender_email: account.email,
        sender_name: account.display_name
      }));
    }
  };

  const loadDefaults = async () => {
    // If we have draft-loaded positions, don't overwrite them
    // The draftInfo state tells us if this form was loaded from a copy/forward
    if (draftInfo && formData.ad_positions && formData.ad_positions.length > 0) {
      console.log('Skipping loadDefaults - draft positions exist');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/ad-proposals/defaults/${formData.proposal_type}?magazine=${formData.magazine}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // For frequency tariff, initialize frequency_positions from the static data
      if (formData.proposal_type === 'standard_tariff_2') {
        setFormData(prev => ({
          ...prev,
          ad_positions: response.data.positions || [],
          terms_conditions: response.data.terms_conditions || prev.terms_conditions,
          frequency_positions: frequencyTariffData[formData.magazine] || []
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          ad_positions: response.data.positions || [],
          terms_conditions: response.data.terms_conditions || prev.terms_conditions,
          frequency_positions: [] // Clear frequency positions for other types
        }));
      }
      
      // Also load default editable content
      setEditableContent(prev => ({
        ...prev,
        introduction: response.data.introduction || prev.introduction,
        why_advertise: response.data.why_advertise || prev.why_advertise,
        terms_conditions: response.data.terms_conditions || prev.terms_conditions
      }));
    } catch (error) {
      console.error('Error loading defaults:', error);
    }
  };

  const handleAddPosition = () => {
    setFormData(prev => ({
      ...prev,
      ad_positions: [
        ...prev.ad_positions,
        { position: 'Full Page (Inside)', page_type: 'Color', insertions: '1 Issue', std_price: 25000, discounted_price: 25000, period: '' }
      ]
    }));
  };

  const handleRemovePosition = (index) => {
    setFormData(prev => ({
      ...prev,
      ad_positions: prev.ad_positions.filter((_, i) => i !== index)
    }));
  };

  const handlePositionChange = (index, field, value) => {
    setFormData(prev => {
      const newPositions = [...prev.ad_positions];
      newPositions[index] = { ...newPositions[index], [field]: value };
      
      // Auto-calculate discounted price if discount % is set
      if (field === 'std_price' && prev.discount_percent > 0) {
        newPositions[index].discounted_price = value * (1 - prev.discount_percent / 100);
      }
      
      return { ...prev, ad_positions: newPositions };
    });
  };

  const handleDiscountChange = (discount) => {
    setFormData(prev => {
      const newPositions = prev.ad_positions.map(pos => ({
        ...pos,
        discounted_price: pos.std_price * (1 - discount / 100)
      }));
      return { ...prev, discount_percent: discount, ad_positions: newPositions };
    });
  };

  const calculateTotal = () => {
    return formData.ad_positions.reduce((sum, pos) => sum + (pos.discounted_price || 0), 0);
  };

  // Fetch exact email preview HTML from backend with editable content
  const fetchPreview = async (useEditedContent = false) => {
    setLoadingPreview(true);
    try {
      // If custom HTML mode is enabled and has content, use it directly
      if (useCustomHtml && customHtml.trim()) {
        setPreviewHtml(customHtml);
        setLoadingPreview(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      
      // Always include content (either edited or current defaults)
      const contentToSend = {
        introduction: editableContent.introduction || '',
        why_advertise: editableContent.why_advertise || '',
        terms_conditions: editableContent.terms_conditions || formData.terms_conditions || '',
        special_remarks: editableContent.special_remarks || formData.special_remarks || '',
        circulation_text: editableContent.circulation_text || '',
        signature_text: editableContent.signature_text || ''
      };
      
      // Build the complete data to send, ensuring all current form values are used
      const dataToSend = {
        ...formData,
        proposal_date: new Date(formData.proposal_date).toLocaleDateString('en-GB'),
        ...contentToSend
      };
      
      // If frequency tariff, include the positions data and settings
      // IMPORTANT: Always use formData.frequency_positions which is updated on user edits
      if (isFrequencyTariff) {
        dataToSend.frequency_positions = formData.frequency_positions || frequencyTariffData[formData.magazine] || [];
        dataToSend.frequency_settings = frequencySettings;
      }
      
      console.log('Sending preview data:', dataToSend); // Debug log
      
      const response = await axios.post(`${API}/ad-proposals/preview`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Open preview modal and fetch HTML
  const handleOpenPreview = async () => {
    if (recipientMode === 'manual') {
      if (!formData.contact_name || !formData.company_name) {
        toast.error('Please fill in recipient details first');
        return;
      }
    } else {
      if (selectedRecipients.length === 0) {
        toast.error('Please select at least one recipient first');
        return;
      }
      // For bulk mode, use first recipient for preview
      const firstRecipient = selectedRecipients[0];
      setFormData(prev => ({
        ...prev,
        contact_name: firstRecipient.contact_person || firstRecipient.name,
        company_name: firstRecipient.company_name || firstRecipient.organization,
        emails: selectedRecipients.map(r => r.email).join(', ')
      }));
    }
    
    // Initialize editable content with defaults ONLY if not already populated
    if (!editableContent.introduction || !editableContent.why_advertise) {
      const defaults = getDefaultContent(formData.magazine);
      setEditableContent({
        introduction: editableContent.introduction || defaults.introduction,
        why_advertise: editableContent.why_advertise || defaults.why_advertise,
        terms_conditions: editableContent.terms_conditions || formData.terms_conditions || defaults.terms_conditions,
        special_remarks: editableContent.special_remarks || formData.special_remarks || defaults.special_remarks,
        circulation_text: editableContent.circulation_text || defaults.circulation_text,
        signature_text: editableContent.signature_text || defaults.signature_text
      });
    }
    
    setEditMode(false);
    setShowPreview(true);
    await fetchPreview(false);
  };

  // Apply edited content and refresh preview
  const handleApplyEdits = async () => {
    setEditMode(false);
    await fetchPreview(true);
    toast.success('Changes applied to preview');
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    setSendingTest(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build complete payload matching preview data
      const testPayload = {
        test_email: testEmail,
        magazine: formData.magazine,
        proposal_type: formData.proposal_type,
        contact_name: formData.contact_name,
        company_name: formData.company_name,
        proposal_date: new Date(formData.proposal_date).toLocaleDateString('en-GB'),
        ad_positions: formData.ad_positions,
        discount_percent: formData.discount_percent,
        // Editable content - MUST match preview
        introduction: editableContent.introduction,
        why_advertise: editableContent.why_advertise,
        terms_conditions: editableContent.terms_conditions,
        special_remarks: editableContent.special_remarks,
        circulation_text: editableContent.circulation_text,
        signature_text: editableContent.signature_text,
        // Attachments
        media_kit_url: formData.media_kit_url,
        banner_image_url: formData.banner_image_url,
        // Frequency tariff data - CRITICAL for correct preview
        frequency_positions: isFrequencyTariff ? (formData.frequency_positions || frequencyTariffData[formData.magazine] || []) : [],
        frequency_settings: isFrequencyTariff ? frequencySettings : {}
      };
      
      await axios.post(`${API}/ad-proposals/test`, testPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSubmit = async () => {
    // Validation based on recipient mode
    if (recipientMode === 'manual') {
      if (!formData.contact_name || !formData.company_name || !formData.emails) {
        toast.error('Please fill in recipient details');
        return;
      }
    } else {
      if (selectedRecipients.length === 0) {
        toast.error('Please select at least one recipient');
        return;
      }
    }
    
    if (formData.ad_positions.length === 0 && !isFrequencyTariff) {
      toast.error('Please add at least one advertisement position');
      return;
    }

    setSending(true);
    setShowPreview(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Start progress tracking
      const totalRecipients = recipientMode === 'manual' ? 1 : selectedRecipients.length;
      const progressResponse = await axios.post(`${API}/email/progress/start`, {
        total: totalRecipients,
        email_type: 'ad_proposal'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = progressResponse.data.job_id;
      setProgressJobId(jobId);
      setShowProgressModal(true);
      
      if (recipientMode === 'manual') {
        // Single recipient - build complete payload matching preview
        const sendPayload = {
          ...formData,
          proposal_date: new Date(formData.proposal_date).toLocaleDateString('en-GB'),
          // Editable content - MUST match preview
          introduction: editableContent.introduction,
          why_advertise: editableContent.why_advertise,
          terms_conditions: editableContent.terms_conditions,
          special_remarks: editableContent.special_remarks,
          circulation_text: editableContent.circulation_text,
          signature_text: editableContent.signature_text,
          // Frequency tariff data - CRITICAL for correct email/PDF
          frequency_positions: isFrequencyTariff ? (formData.frequency_positions || frequencyTariffData[formData.magazine] || []) : [],
          frequency_settings: isFrequencyTariff ? frequencySettings : {},
          job_id: jobId
        };
        
        const response = await axios.post(`${API}/ad-proposals`, sendPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success(`Proposal sent successfully! (${response.data.sent_count} emails sent)`);
      } else {
        // Bulk send to selected recipients
        let successCount = 0;
        let failCount = 0;
        
        for (const recipient of selectedRecipients) {
          try {
            // Build complete payload for each recipient - MUST match preview
            const recipientData = {
              ...formData,
              contact_name: recipient.contact_person || recipient.name,
              company_name: recipient.company_name || recipient.organization,
              emails: recipient.email,
              proposal_date: new Date(formData.proposal_date).toLocaleDateString('en-GB'),
              // Editable content
              introduction: editableContent.introduction,
              why_advertise: editableContent.why_advertise,
              terms_conditions: editableContent.terms_conditions,
              special_remarks: editableContent.special_remarks,
              circulation_text: editableContent.circulation_text,
              signature_text: editableContent.signature_text,
              // Frequency tariff data - CRITICAL
              frequency_positions: isFrequencyTariff ? (formData.frequency_positions || frequencyTariffData[formData.magazine] || []) : [],
              frequency_settings: isFrequencyTariff ? frequencySettings : {},
              job_id: jobId
            };
            
            await axios.post(`${API}/ad-proposals`, recipientData, {
              headers: { Authorization: `Bearer ${token}` }
            });
            successCount++;
          } catch (err) {
            failCount++;
            console.error(`Failed to send to ${recipient.email}:`, err);
          }
        }
        
        if (successCount > 0) {
          toast.success(`Proposals sent: ${successCount} successful, ${failCount} failed`);
        } else {
          toast.error('Failed to send proposals');
        }
      }
      
      // Reset selections
      setSelectedRecipients([]);
      setFormData(prev => ({
        ...prev,
        contact_name: '',
        company_name: '',
        emails: '',
        special_remarks: ''
      }));
      
    } catch (error) {
      console.error('Error sending proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to send proposal');
      setShowProgressModal(false);
    } finally {
      setSending(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#7c3aed' }}>
              Advertisement Proposal
            </h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/ad-proposal-reports')}>
            <FileText className="mr-2" size={18} />
            View Reports
          </Button>
        </div>

        {/* Form */}
        <div className="form-section mb-8">
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#7c3aed' }}>Create New Proposal</h2>
          
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <Label>Magazine</Label>
              <select
                value={formData.magazine}
                onChange={(e) => setFormData({ ...formData, magazine: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="Aabkari Times">Aabkari Times</option>
                <option value="Sugar Times">Sugar Times</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Proposal Type</Label>
              <select
                value={formData.proposal_type}
                onChange={(e) => setFormData({ ...formData, proposal_type: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {proposalTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sender Email Selection */}
          <div className="bg-indigo-50 p-4 rounded-lg mb-6 border border-indigo-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Label className="font-semibold text-indigo-800">Send From:</Label>
                <select
                  value={selectedSender?.id || ''}
                  onChange={(e) => handleSenderChange(e.target.value)}
                  className="h-10 w-72 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm"
                >
                  {senderAccounts.length === 0 ? (
                    <option value="">No sender accounts configured</option>
                  ) : (
                    senderAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.display_name} ({account.email})
                      </option>
                    ))
                  )}
                </select>
              </div>
              {senderAccounts.length === 0 && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => navigate('/email-settings')}
                  className="text-indigo-700 border-indigo-300"
                >
                  <Settings className="mr-1" size={14} />
                  Configure Senders
                </Button>
              )}
            </div>
            {selectedSender && (
              <p className="text-xs text-indigo-600 mt-2">
                Emails will be sent from: <strong>{selectedSender.email}</strong>
              </p>
            )}
          </div>

          {/* Template Selection & PDF Options */}
          <div className="bg-emerald-50 p-4 rounded-lg mb-6 border border-emerald-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Load from Template */}
                <div className="flex items-center space-x-2">
                  <Label className="font-semibold text-emerald-800">Load Template:</Label>
                  <select
                    onChange={(e) => loadTemplate(e.target.value)}
                    className="h-10 w-64 rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm"
                    defaultValue=""
                  >
                    <option value="">-- Select a saved template --</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.template_name} ({template.template_category})
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Auto PDF Attachment Toggle */}
                <label className="flex items-center space-x-2 text-emerald-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attachPdf}
                    onChange={(e) => setAttachPdf(e.target.checked)}
                    className="h-4 w-4 rounded border-emerald-300"
                  />
                  <span className="text-sm font-medium">Attach PDF Proposal</span>
                </label>
              </div>
              
              {/* Save as Template Button */}
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => setShowSaveTemplate(true)}
                className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
              >
                <FileText className="mr-1" size={14} />
                Save as Template
              </Button>
            </div>
            
            {templates.length === 0 && (
              <p className="text-xs text-emerald-600 mt-2">
                No templates saved yet. Fill out the form and click &quot;Save as Template&quot; to create reusable templates.
              </p>
            )}
          </div>

          {/* Save Template Modal */}
          {showSaveTemplate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 w-96">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Save as Template</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Template Name</Label>
                    <Input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      placeholder="e.g., Republic Day 2026, Standard Proposal"
                    />
                  </div>
                  <p className="text-sm text-gray-600">
                    This will save the current ad positions, terms, remarks, and any custom content as a reusable template.
                  </p>
                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}>
                      Cancel
                    </Button>
                    <Button onClick={saveAsTemplate} style={{ backgroundColor: '#059669' }}>
                      Save Template
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Banner Image URL for Special Issues */}
          {isSpecialIssue && (
            <div className="bg-orange-50 p-4 rounded-lg mb-6 border border-orange-200">
              <h3 className="font-semibold text-orange-800 mb-3">🎨 Special Issue Banner (Optional)</h3>
              <p className="text-sm text-orange-700 mb-3">
                Add a themed banner image for special issues (e.g., Republic Day, Diwali). 
                <br/><strong>Recommended size: 700px × 200px</strong>
              </p>
              <div className="space-y-2">
                <Label>Banner Image URL</Label>
                <Input 
                  value={formData.banner_image_url}
                  onChange={(e) => setFormData({ ...formData, banner_image_url: e.target.value })}
                  placeholder="https://example.com/republic-day-banner.jpg"
                />
              </div>
              {formData.banner_image_url && (
                <div className="mt-3">
                  <p className="text-xs text-orange-600 mb-2">Preview:</p>
                  <img 
                    src={formData.banner_image_url} 
                    alt="Banner Preview" 
                    className="max-w-full h-auto rounded border"
                    style={{ maxHeight: '100px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Recipient Selection */}
          <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-blue-800 flex items-center">
                <Users className="mr-2" size={18} />
                Select Recipients
              </h3>
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  variant={recipientMode === 'manual' ? 'default' : 'outline'}
                  onClick={() => handleRecipientModeChange('manual')}
                  className={recipientMode === 'manual' ? 'bg-blue-600' : ''}
                >
                  <UserPlus className="mr-1" size={14} />
                  Manual Entry
                </Button>
                <Button
                  size="sm"
                  variant={recipientMode === 'members' ? 'default' : 'outline'}
                  onClick={() => handleRecipientModeChange('members')}
                  className={recipientMode === 'members' ? 'bg-green-600' : ''}
                >
                  <Users className="mr-1" size={14} />
                  From Members
                </Button>
                <Button
                  size="sm"
                  variant={recipientMode === 'advertisers' ? 'default' : 'outline'}
                  onClick={() => handleRecipientModeChange('advertisers')}
                  className={recipientMode === 'advertisers' ? 'bg-purple-600' : ''}
                >
                  <Building className="mr-1" size={14} />
                  From Advertisers
                </Button>
              </div>
            </div>

            {/* Manual Entry Mode */}
            {recipientMode === 'manual' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input 
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Mr. John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input 
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="ABC Distilleries Pvt. Ltd."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email(s) * <span className="text-gray-500 text-xs">(comma-separated)</span></Label>
                  <Input 
                    value={formData.emails}
                    onChange={(e) => setFormData({ ...formData, emails: e.target.value })}
                    placeholder="john@example.com, sales@example.com"
                  />
                </div>
              </div>
            )}

            {/* Members Selection Mode */}
            {recipientMode === 'members' && (
              <div>
                <div className="flex items-center space-x-3 mb-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input 
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      placeholder="Search members by name, organization, or email..."
                      className="pl-9"
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => selectAllFiltered(members, 'member')}>
                    Select All ({getFilteredList(members).length})
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearAllSelections}>
                    Clear
                  </Button>
                  <Button size="sm" variant="outline" onClick={fetchMembers} disabled={loadingRecipients}>
                    <RefreshCw className={loadingRecipients ? 'animate-spin' : ''} size={14} />
                  </Button>
                </div>
                
                {selectedRecipients.length > 0 && (
                  <div className="bg-green-100 p-2 rounded mb-3 flex items-center">
                    <CheckSquare className="text-green-600 mr-2" size={16} />
                    <span className="text-green-800 text-sm font-medium">{selectedRecipients.length} recipient(s) selected</span>
                  </div>
                )}
                
                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                  {loadingRecipients ? (
                    <div className="p-4 text-center text-gray-500">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={20} />
                      Loading members...
                    </div>
                  ) : getFilteredList(members).length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No members found</div>
                  ) : (
                    getFilteredList(members).map((member, idx) => {
                      const id = member.id || member.member_id;
                      const isSelected = selectedRecipients.some(r => (r.id || r.member_id) === id);
                      return (
                        <div 
                          key={id || idx}
                          className={`p-2 border-b cursor-pointer hover:bg-gray-50 flex items-center ${isSelected ? 'bg-green-50' : ''}`}
                          onClick={() => toggleRecipient(member, 'member')}
                        >
                          {isSelected ? <CheckSquare className="text-green-600 mr-2" size={16} /> : <Square className="text-gray-400 mr-2" size={16} />}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{member.name || member.contact_person}</p>
                            <p className="text-xs text-gray-500">{member.organization || member.company_name} | {member.email}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${(member.magazine === 'Aabkari Times' || member.magazine === 'Aabkari Times ENG') ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                            {member.magazine}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Advertisers Selection Mode */}
            {recipientMode === 'advertisers' && (
              <div>
                <div className="flex items-center space-x-3 mb-3">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <Input 
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      placeholder="Search advertisers by name, company, or email..."
                      className="pl-9"
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => selectAllFiltered(advertisers, 'advertiser')}>
                    Select All ({getFilteredList(advertisers).length})
                  </Button>
                  <Button size="sm" variant="outline" onClick={clearAllSelections}>
                    Clear
                  </Button>
                  <Button size="sm" variant="outline" onClick={fetchAdvertisers} disabled={loadingRecipients}>
                    <RefreshCw className={loadingRecipients ? 'animate-spin' : ''} size={14} />
                  </Button>
                </div>
                
                {selectedRecipients.length > 0 && (
                  <div className="bg-purple-100 p-2 rounded mb-3 flex items-center">
                    <CheckSquare className="text-purple-600 mr-2" size={16} />
                    <span className="text-purple-800 text-sm font-medium">{selectedRecipients.length} recipient(s) selected</span>
                  </div>
                )}
                
                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                  {loadingRecipients ? (
                    <div className="p-4 text-center text-gray-500">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={20} />
                      Loading advertisers...
                    </div>
                  ) : getFilteredList(advertisers).length === 0 ? (
                    <div className="p-4 text-center text-gray-500">No advertisers found</div>
                  ) : (
                    getFilteredList(advertisers).map((advertiser, idx) => {
                      const id = advertiser.id || advertiser.advertiser_id;
                      const isSelected = selectedRecipients.some(r => (r.id || r.advertiser_id) === id);
                      return (
                        <div 
                          key={id || idx}
                          className={`p-2 border-b cursor-pointer hover:bg-gray-50 flex items-center ${isSelected ? 'bg-purple-50' : ''}`}
                          onClick={() => toggleRecipient(advertiser, 'advertiser')}
                        >
                          {isSelected ? <CheckSquare className="text-purple-600 mr-2" size={16} /> : <Square className="text-gray-400 mr-2" size={16} />}
                          <div className="flex-1">
                            <p className="font-medium text-sm">{advertiser.contact_person || advertiser.name}</p>
                            <p className="text-xs text-gray-500">{advertiser.company_name} | {advertiser.email}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded ${(advertiser.magazine === 'Aabkari Times' || advertiser.magazine === 'Aabkari Times ENG') ? 'bg-purple-100 text-purple-700' : 'bg-green-100 text-green-700'}`}>
                            {advertiser.magazine}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {/* Date Row */}
            <div className="mt-4">
              <div className="space-y-2 w-1/2">
                <Label>Proposal Date</Label>
                <Input 
                  type="date"
                  value={formData.proposal_date}
                  onChange={(e) => setFormData({ ...formData, proposal_date: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Advertisement Positions - Different view for Frequency Tariff */}
          {isFrequencyTariff ? (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    Advertisement Tariff - {formData.magazine}
                    <span className="text-sm font-normal text-gray-500 ml-2">(Frequency Discounts)</span>
                  </h3>
                  <p className="text-sm text-gray-600">
                    Edit positions and prices. Toggle columns and adjust discount percentages below.
                  </p>
                </div>
                <Button onClick={() => {
                  const newPosition = {
                    position: 'New Position',
                    oneMonth: 25000,
                    threeMonths: Math.round(25000 * 3 * (1 - frequencySettings.threeMonthDiscount/100)),
                    sixMonths: Math.round(25000 * 6 * (1 - frequencySettings.sixMonthDiscount/100)),
                    oneYear: Math.round(25000 * 12 * (1 - frequencySettings.oneYearDiscount/100))
                  };
                  setFormData(prev => ({
                    ...prev,
                    frequency_positions: [...(prev.frequency_positions || frequencyTariffData[formData.magazine] || []), newPosition]
                  }));
                }} size="sm" style={{ backgroundColor: '#7c3aed' }}>
                  <Plus className="mr-1" size={16} />
                  Add Position
                </Button>
              </div>
              
              {/* Column Toggle & Discount Settings */}
              <div className="bg-white p-3 rounded border mb-4">
                <div className="flex flex-wrap gap-4 items-center">
                  <span className="text-sm font-medium text-gray-700">Show Columns:</span>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={frequencySettings.showThreeMonth}
                      onChange={(e) => setFrequencySettings(prev => ({ ...prev, showThreeMonth: e.target.checked }))}
                      className="mr-1"
                    />
                    3 Months
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={frequencySettings.threeMonthDiscount}
                      onChange={(e) => setFrequencySettings(prev => ({ ...prev, threeMonthDiscount: parseFloat(e.target.value) || 0 }))}
                      className="w-14 ml-1 text-center text-xs"
                      placeholder="%"
                    />
                    <span className="text-xs text-gray-500 ml-1">% off</span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={frequencySettings.showSixMonth}
                      onChange={(e) => setFrequencySettings(prev => ({ ...prev, showSixMonth: e.target.checked }))}
                      className="mr-1"
                    />
                    6 Months
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={frequencySettings.sixMonthDiscount}
                      onChange={(e) => setFrequencySettings(prev => ({ ...prev, sixMonthDiscount: parseFloat(e.target.value) || 0 }))}
                      className="w-14 ml-1 text-center text-xs"
                      placeholder="%"
                    />
                    <span className="text-xs text-gray-500 ml-1">% off</span>
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      checked={frequencySettings.showOneYear}
                      onChange={(e) => setFrequencySettings(prev => ({ ...prev, showOneYear: e.target.checked }))}
                      className="mr-1"
                    />
                    1 Year
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={frequencySettings.oneYearDiscount}
                      onChange={(e) => setFrequencySettings(prev => ({ ...prev, oneYearDiscount: parseFloat(e.target.value) || 0 }))}
                      className="w-14 ml-1 text-center text-xs"
                      placeholder="%"
                    />
                    <span className="text-xs text-gray-500 ml-1">% off</span>
                  </label>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border">
                  <thead>
                    <tr className="bg-purple-700 text-white">
                      <th className="p-3 text-left font-semibold">POSITIONS</th>
                      <th className="p-3 text-center font-semibold">ONE MONTH</th>
                      {frequencySettings.showThreeMonth && (
                        <th className="p-3 text-center font-semibold">3 MONTHS (-{frequencySettings.threeMonthDiscount}%)</th>
                      )}
                      {frequencySettings.showSixMonth && (
                        <th className="p-3 text-center font-semibold">6 MONTHS (-{frequencySettings.sixMonthDiscount}%)</th>
                      )}
                      {frequencySettings.showOneYear && (
                        <th className="p-3 text-center font-semibold">1 YEAR (-{frequencySettings.oneYearDiscount}%)</th>
                      )}
                      <th className="p-3 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData.frequency_positions || frequencyTariffData[formData.magazine] || []).map((row, index) => (
                      <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="p-2">
                          <Input
                            value={row.position}
                            onChange={(e) => {
                              const positions = [...(formData.frequency_positions || frequencyTariffData[formData.magazine] || [])];
                              positions[index] = { ...positions[index], position: e.target.value };
                              setFormData(prev => ({ ...prev, frequency_positions: positions }));
                            }}
                            className="w-40"
                          />
                        </td>
                        <td className="p-2 text-center">
                          <Input
                            type="number"
                            value={row.oneMonth}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const positions = [...(formData.frequency_positions || frequencyTariffData[formData.magazine] || [])];
                              positions[index] = { 
                                ...positions[index], 
                                oneMonth: val,
                                threeMonths: Math.round(val * 3 * (1 - frequencySettings.threeMonthDiscount/100)),
                                sixMonths: Math.round(val * 6 * (1 - frequencySettings.sixMonthDiscount/100)),
                                oneYear: Math.round(val * 12 * (1 - frequencySettings.oneYearDiscount/100))
                              };
                              setFormData(prev => ({ ...prev, frequency_positions: positions }));
                            }}
                            className="w-28 text-right"
                          />
                        </td>
                        {frequencySettings.showThreeMonth && (
                          <td className="p-2 text-center">
                            <Input
                              type="number"
                              value={row.threeMonths || Math.round(row.oneMonth * 3 * (1 - frequencySettings.threeMonthDiscount/100))}
                              onChange={(e) => {
                                const positions = [...(formData.frequency_positions || frequencyTariffData[formData.magazine] || [])];
                                positions[index] = { ...positions[index], threeMonths: parseFloat(e.target.value) || 0 };
                                setFormData(prev => ({ ...prev, frequency_positions: positions }));
                              }}
                              className="w-28 text-right"
                            />
                            <div className="text-xs text-gray-500">(Rs. {Math.round((row.threeMonths || row.oneMonth * 3 * (1 - frequencySettings.threeMonthDiscount/100))/3).toLocaleString('en-IN')}/mo)</div>
                          </td>
                        )}
                        {frequencySettings.showSixMonth && (
                          <td className="p-2 text-center">
                            <Input
                              type="number"
                              value={row.sixMonths}
                              onChange={(e) => {
                                const positions = [...(formData.frequency_positions || frequencyTariffData[formData.magazine] || [])];
                                positions[index] = { ...positions[index], sixMonths: parseFloat(e.target.value) || 0 };
                                setFormData(prev => ({ ...prev, frequency_positions: positions }));
                              }}
                              className="w-28 text-right"
                            />
                            <div className="text-xs text-gray-500">(Rs. {Math.round(row.sixMonths/6).toLocaleString('en-IN')}/mo)</div>
                          </td>
                        )}
                        {frequencySettings.showOneYear && (
                          <td className="p-2 text-center">
                            <Input
                              type="number"
                              value={row.oneYear}
                              onChange={(e) => {
                                const positions = [...(formData.frequency_positions || frequencyTariffData[formData.magazine] || [])];
                                positions[index] = { ...positions[index], oneYear: parseFloat(e.target.value) || 0 };
                                setFormData(prev => ({ ...prev, frequency_positions: positions }));
                              }}
                              className="w-28 text-right"
                            />
                            <div className="text-xs text-gray-500">(Rs. {Math.round(row.oneYear/12).toLocaleString('en-IN')}/mo)</div>
                          </td>
                        )}
                        <td className="p-2">
                          <button 
                            onClick={() => {
                              const positions = [...(formData.frequency_positions || frequencyTariffData[formData.magazine] || [])];
                              positions.splice(index, 1);
                              setFormData(prev => ({ ...prev, frequency_positions: positions }));
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">* All prices are exclusive of GST (5%)</p>
            </div>
          ) : (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">Advertisement Positions</h3>
              <Button onClick={handleAddPosition} size="sm" style={{ backgroundColor: '#7c3aed' }}>
                <Plus className="mr-1" size={16} />
                Add Position
              </Button>
            </div>
            
            {formData.ad_positions.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No positions added. Click &quot;Add Position&quot; to start.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-2 text-left">Position</th>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Insertions</th>
                      <th className="p-2 text-right">Std. Price (₹)</th>
                      <th className="p-2 text-right">Discount %</th>
                      <th className="p-2 text-right">Offer Price (₹)</th>
                      <th className="p-2 text-left">Period</th>
                      <th className="p-2 text-right">Total (₹)</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.ad_positions.map((pos, index) => {
                      const rowTotal = (pos.discounted_price || pos.std_price || 0);
                      return (
                        <tr key={index} className="border-b">
                          <td className="p-2">
                            <select
                              value={pos.position}
                              onChange={(e) => handlePositionChange(index, 'position', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            >
                              {positionOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                          <td className="p-2">
                            <select
                              value={pos.page_type}
                              onChange={(e) => handlePositionChange(index, 'page_type', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            >
                              {pageTypeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                          <td className="p-2">
                            <select
                              value={pos.insertions}
                              onChange={(e) => handlePositionChange(index, 'insertions', e.target.value)}
                              className="w-full border rounded px-2 py-1 text-sm"
                            >
                              {insertionOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                          </td>
                          <td className="p-2">
                            <Input 
                              type="number"
                              value={pos.std_price}
                              onChange={(e) => handlePositionChange(index, 'std_price', parseFloat(e.target.value) || 0)}
                              className="w-24 text-right"
                            />
                          </td>
                          <td className="p-2">
                            <Input 
                              type="number"
                              min="0"
                              max="100"
                              value={pos.discount_percent || 0}
                              onChange={(e) => {
                                const discount = parseFloat(e.target.value) || 0;
                                const newPrice = pos.std_price * (1 - discount / 100);
                                handlePositionChange(index, 'discount_percent', discount);
                                handlePositionChange(index, 'discounted_price', newPrice);
                              }}
                              className="w-20 text-right"
                            />
                          </td>
                          <td className="p-2">
                            <Input 
                              type="number"
                              value={pos.discounted_price}
                              onChange={(e) => handlePositionChange(index, 'discounted_price', parseFloat(e.target.value) || 0)}
                              className="w-24 text-right font-semibold"
                            />
                          </td>
                          <td className="p-2">
                            <Input 
                              value={pos.period}
                              onChange={(e) => handlePositionChange(index, 'period', e.target.value)}
                              placeholder="Jan 25 - Dec 25"
                              className="w-32"
                            />
                          </td>
                          <td className="p-2 text-right font-semibold text-purple-700">
                            {formatCurrency(rowTotal)}
                          </td>
                          <td className="p-2">
                            <button 
                              onClick={() => handleRemovePosition(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Terms & Attachments */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <Label>Terms & Conditions</Label>
              <Textarea 
                value={formData.terms_conditions}
                onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                rows={6}
                placeholder="Enter terms and conditions..."
              />
            </div>
            <div className="space-y-2">
              <Label>Special Remarks</Label>
              <Textarea 
                value={formData.special_remarks}
                onChange={(e) => setFormData({ ...formData, special_remarks: e.target.value })}
                rows={3}
                placeholder="Any special notes or remarks for this proposal..."
              />
              
              <div className="mt-4 space-y-3">
                {/* Media Kit Upload */}
                <div className="space-y-2">
                  <Label className="text-sm">Media Kit (PDF) - Max 100MB</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.media_kit_url}
                      onChange={(e) => setFormData({ ...formData, media_kit_url: e.target.value })}
                      placeholder="URL or upload PDF..."
                      className="flex-1"
                    />
                    <input
                      type="file"
                      id="mediaKitUpload"
                      accept=".pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 100 * 1024 * 1024) {
                          toast.error('File size exceeds 100MB limit');
                          return;
                        }
                        try {
                          const formDataUpload = new FormData();
                          formDataUpload.append('file', file);
                          const token = localStorage.getItem('token');
                          const response = await axios.post(`${API}/upload/ad_proposals/media_kits`, formDataUpload, {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({ ...prev, media_kit_url: response.data.url }));
                          toast.success('Media kit uploaded!');
                        } catch (error) {
                          console.error('Upload error:', error);
                          toast.error('Failed to upload media kit');
                        }
                      }}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('mediaKitUpload').click()}
                    >
                      <FileText className="mr-1" size={14} /> Upload
                    </Button>
                  </div>
                </div>
                
                {/* Cover Letter Upload */}
                <div className="space-y-2">
                  <Label className="text-sm">Cover Letter (PDF) - Max 50MB</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.cover_letter_url}
                      onChange={(e) => setFormData({ ...formData, cover_letter_url: e.target.value })}
                      placeholder="URL or upload PDF..."
                      className="flex-1"
                    />
                    <input
                      type="file"
                      id="coverLetterUpload"
                      accept=".pdf"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 50 * 1024 * 1024) {
                          toast.error('File size exceeds 50MB limit');
                          return;
                        }
                        try {
                          const formDataUpload = new FormData();
                          formDataUpload.append('file', file);
                          const token = localStorage.getItem('token');
                          const response = await axios.post(`${API}/upload/ad_proposals/cover_letters`, formDataUpload, {
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                          });
                          setFormData(prev => ({ ...prev, cover_letter_url: response.data.url }));
                          toast.success('Cover letter uploaded!');
                        } catch (error) {
                          console.error('Upload error:', error);
                          toast.error('Failed to upload cover letter');
                        }
                      }}
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('coverLetterUpload').click()}
                    >
                      <FileText className="mr-1" size={14} /> Upload
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Custom HTML Option */}
          <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h3 className="font-semibold text-gray-800 mr-4">Email Mode</h3>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCustomHtml}
                    onChange={(e) => setUseCustomHtml(e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-600">Use Custom HTML</span>
                </label>
              </div>
            </div>
            
            {useCustomHtml && (
              <div className="space-y-2">
                <Label>Custom HTML Email Content</Label>
                <Textarea 
                  value={customHtml}
                  onChange={(e) => setCustomHtml(e.target.value)}
                  rows={12}
                  placeholder="<html>&#10;<body>&#10;  <h1>Your custom email content...</h1>&#10;</body>&#10;</html>"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  Paste your custom HTML email content here. This will override the standard template.
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <Button 
              variant="outline"
              onClick={handleOpenPreview}
              disabled={
                recipientMode === 'manual' 
                  ? (!formData.contact_name || !formData.company_name)
                  : (selectedRecipients.length === 0)
              }
            >
              <Eye className="mr-2" size={18} />
              Preview & Send
            </Button>
          </div>
        </div>

        {/* Preview Modal - Shows Exact Email with Edit Capability */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden">
              <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Eye className="mr-2" size={24} />
                  {editMode ? 'Edit Email Content' : 'Email Preview'} - {formData.magazine}
                </h2>
                <div className="flex items-center space-x-2">
                  {!editMode ? (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setEditMode(true)}
                      className="text-white hover:bg-purple-500"
                    >
                      <Edit3 className="mr-1" size={16} />
                      Edit Content
                    </Button>
                  ) : (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={handleApplyEdits}
                      className="text-white hover:bg-purple-500 bg-green-600"
                    >
                      <Save className="mr-1" size={16} />
                      Apply Changes
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => fetchPreview(editMode)}
                    disabled={loadingPreview}
                    className="text-white hover:bg-purple-500"
                  >
                    <RefreshCw className={`mr-1 ${loadingPreview ? 'animate-spin' : ''}`} size={16} />
                    Refresh
                  </Button>
                  <button onClick={() => { setShowPreview(false); setEditMode(false); }} className="text-white hover:text-gray-200">
                    <X size={24} />
                  </button>
                </div>
              </div>
              
              <div className="flex h-[calc(95vh-180px)]">
                {/* Edit Panel - Left Side */}
                {editMode && (
                  <div className="w-1/2 border-r bg-gray-50 p-4 overflow-y-auto">
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800 flex items-center">
                          <Edit3 className="mr-2" size={16} />
                          Edit the content below. Click &quot;Apply Changes&quot; to update the preview.
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">Introduction Text</Label>
                        <Textarea 
                          value={editableContent.introduction}
                          onChange={(e) => setEditableContent({...editableContent, introduction: e.target.value})}
                          rows={4}
                          className="text-sm"
                          placeholder="Greetings from..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">Why Advertise With Us?</Label>
                        <Textarea 
                          value={editableContent.why_advertise}
                          onChange={(e) => setEditableContent({...editableContent, why_advertise: e.target.value})}
                          rows={3}
                          className="text-sm"
                          placeholder="Our magazine has..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">Terms & Conditions</Label>
                        <Textarea 
                          value={editableContent.terms_conditions}
                          onChange={(e) => setEditableContent({...editableContent, terms_conditions: e.target.value})}
                          rows={5}
                          className="text-sm"
                          placeholder="1. Payment terms..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">Special Remarks</Label>
                        <Textarea 
                          value={editableContent.special_remarks}
                          onChange={(e) => setEditableContent({...editableContent, special_remarks: e.target.value})}
                          rows={2}
                          className="text-sm"
                          placeholder="Any special notes..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">Circulation & Reach Text</Label>
                        <Textarea 
                          value={editableContent.circulation_text}
                          onChange={(e) => setEditableContent({...editableContent, circulation_text: e.target.value})}
                          rows={2}
                          className="text-sm"
                          placeholder="Circulation: 16,700+ copies per month | Reach: ..."
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="font-semibold">Signature Text (HTML allowed)</Label>
                        <Textarea 
                          value={editableContent.signature_text}
                          onChange={(e) => setEditableContent({...editableContent, signature_text: e.target.value})}
                          rows={3}
                          className="text-sm font-mono"
                          placeholder="Best Regards,<br/><strong>Name</strong>"
                        />
                        {editableContent.signature_text && (
                          <div className="p-2 bg-gray-50 border rounded text-sm">
                            <p className="text-xs text-gray-500 mb-1">Preview:</p>
                            <div dangerouslySetInnerHTML={{ __html: editableContent.signature_text }} />
                          </div>
                        )}
                      </div>
                      
                      <Button 
                        onClick={handleApplyEdits}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Save className="mr-2" size={16} />
                        Apply Changes & Update Preview
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Email Preview - Right Side (or full width when not editing) */}
                <div className={`${editMode ? 'w-1/2' : 'w-full'} overflow-auto bg-gray-100 p-4`}>
                  {loadingPreview ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <RefreshCw className="animate-spin mx-auto mb-3" size={32} />
                        <p className="text-gray-600">Loading email preview...</p>
                      </div>
                    </div>
                  ) : previewHtml ? (
                    <div className="bg-white rounded-lg shadow-lg mx-auto" style={{ maxWidth: '700px' }}>
                      <div 
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                        className="email-preview"
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">Failed to load preview</p>
                    </div>
                  )}
                </div>

                {/* Test Email Section */}
                <div className="bg-yellow-50 p-4 border-t border-yellow-200">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <TestTube size={20} className="text-yellow-700" />
                      <span className="font-semibold text-yellow-800">Send Test Email:</span>
                    </div>
                    <Input 
                      type="email"
                      placeholder="Enter your email address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1 min-w-[300px]"
                    />
                    <Button 
                      onClick={handleSendTestEmail}
                      disabled={sendingTest || !testEmail}
                      variant="outline"
                      className="border-yellow-400 text-yellow-700 hover:bg-yellow-100"
                    >
                      <Send className="mr-2" size={16} />
                      {sendingTest ? 'Sending...' : 'Send Test'}
                    </Button>
                  </div>
                  <p className="text-xs text-yellow-700 mt-2">
                    Send a test email to yourself first to verify the email looks correct before sending to recipients.
                  </p>
                </div>
                
                {/* Attachments Info Section */}
                <div className="bg-blue-50 p-3 border-t border-blue-200">
                  <div className="flex items-center flex-wrap gap-3">
                    <span className="font-semibold text-blue-800 text-sm flex items-center">
                      📎 Attachments:
                    </span>
                    {attachPdf && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        📄 PDF Proposal (auto-generated)
                      </span>
                    )}
                    {formData.media_kit_url && (
                      <a 
                        href={formData.media_kit_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs hover:bg-green-200 flex items-center"
                      >
                        📁 Media Kit
                      </a>
                    )}
                    {formData.cover_letter_url && (
                      <a 
                        href={formData.cover_letter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 flex items-center"
                      >
                        📝 Cover Letter
                      </a>
                    )}
                    {!attachPdf && !formData.media_kit_url && !formData.cover_letter_url && (
                      <span className="text-gray-500 text-xs italic">No attachments</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-100 p-4 flex justify-between items-center border-t">
                <div className="text-sm text-gray-600">
                  <strong>To:</strong> {formData.emails}
                  {selectedSender && (
                    <span className="ml-4 text-indigo-600">
                      <strong>Sent by:</strong> {selectedSender.display_name} ({selectedSender.email})
                    </span>
                  )}
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setShowPreview(false)} className="text-gray-600">
                    <X className="mr-2" size={18} />
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    disabled={sending || (!formData.emails && selectedRecipients.length === 0)}
                    style={{ backgroundColor: '#059669' }}
                  >
                    {sending ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={18} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={18} />
                        Send Proposal
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Sending Progress Modal */}
        <SendingProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          jobId={progressJobId}
          totalRecipients={recipientMode === 'manual' ? 1 : selectedRecipients.length}
          emailType="Ad Proposal"
        />
      </div>
    </div>
  );
};

export default AdvertisementProposal;
